// src/scripts/mailer/mailerPdf.js
import { jsPDF } from "jspdf";
import {
  getMailerImageData,
  getMailerProofGuest,
} from "@/scripts/sqlApiHelper/mailerApi";

function clean(value) {
  return String(value ?? "").trim();
}

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function stripUnsafeUrl(value) {
  const text = clean(value);

  if (!/^https?:\/\//i.test(text)) {
    return "";
  }

  return text;
}

function dataUrlFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ""));

    reader.onerror = () =>
      reject(reader.error || new Error("Could not read image."));

    reader.readAsDataURL(blob);
  });
}

/**
 * Resolve an image into a data URL for jsPDF.
 *
 * Primary path:
 *   Use the authenticated mailer callable and the saved Firebase Storage path.
 *   This avoids browser CORS restrictions on Firebase Storage download URLs.
 *
 * Fallback path:
 *   Directly fetch the public image URL. This keeps older templates working if
 *   they contain an imageUrl but no storagePath.
 */
async function loadImageAsDataUrl({
  url,
  storagePath,
  venueId,
}) {
  const path = clean(storagePath);
  const cleanVenueId = clean(venueId);

  if (path && cleanVenueId) {
    try {
      const result = await getMailerImageData(cleanVenueId, path);
      const dataUrl = clean(result?.dataUrl);

      if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(dataUrl)) {
        return dataUrl;
      }
    } catch (error) {
      console.warn(
        "[mailerPdf] Could not load image through mailerApi; trying URL fallback.",
        error
      );
    }
  }

  const safe = stripUnsafeUrl(url);

  if (!safe) {
    return "";
  }

  try {
    const response = await fetch(safe, {
      mode: "cors",
    });

    if (!response.ok) {
      return "";
    }

    return await dataUrlFromBlob(await response.blob());
  } catch (error) {
    console.warn("[mailerPdf] Direct image fetch failed.", error);
    return "";
  }
}

function imageFormatFromDataUrl(dataUrl) {
  const match = /^data:image\/([^;]+);/i.exec(dataUrl);
  const format = String(match?.[1] || "jpeg").toLowerCase();

  if (format.includes("png")) {
    return "PNG";
  }

  if (format.includes("webp")) {
    return "WEBP";
  }

  return "JPEG";
}

function templateTitle(template) {
  return clean(template?.name) || "Email template";
}


function mergeProofValue(value, context = {}) {
  let output = String(value ?? "");

  Object.entries(context).forEach(([key, replacement]) => {
    output = output.replace(
      new RegExp(`{{\\s*${key}\\s*}}`, "gi"),
      String(replacement ?? "")
    );
  });

  return output;
}

export async function downloadMailerTemplatePdf(template, options = {}) {
  let previewGuest = null;

  if (clean(options.venueId)) {
    try {
      previewGuest = await getMailerProofGuest(options.venueId);
    } catch (error) {
      console.warn(
        "[mailerPdf] Could not load a guest for merge-field proofing.",
        error
      );
    }
  }

  const mergeContext = {
    first_name: clean(previewGuest?.first_name) || "[first_name]",
    last_name: clean(previewGuest?.last_name) || "[last_name]",
    email: clean(previewGuest?.email) || "[email]",
    venue_name:
      clean(options.venueName) ||
      clean(options.venueId) ||
      "[venue_name]",
    unsubscribe_url: "[unsubscribe_url]",
  };

  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;

  let y = 18;

  function newPageIfNeeded(heightNeeded) {
    if (y + heightNeeded <= pageHeight - 18) {
      return;
    }

    pdf.addPage();
    y = 18;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text(templateTitle(template), margin, y);

  y += 8;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(90);

  pdf.text(
    `Venue: ${clean(options.venueName) || clean(options.venueId) || "—"}`,
    margin,
    y
  );

  y += 5;

  pdf.text(
    `Subject: ${
      clean(
        mergeProofValue(
          template?.subject,
          mergeContext
        )
      ) || "—"
    }`,
    margin,
    y
  );

  y += 5;

  if (clean(mergeProofValue(template?.preheader, mergeContext))) {
    const preheaderLines = pdf.splitTextToSize(
      `Preheader: ${clean(
        mergeProofValue(
          template.preheader,
          mergeContext
        )
      )}`,
      contentWidth
    );

    pdf.text(preheaderLines, margin, y);
    y += preheaderLines.length * 4 + 2;
  }

  pdf.setDrawColor(190);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 7;

  const blocks = Array.isArray(template?.blocks) ? template.blocks : [];

  for (const block of blocks) {
    const type = clean(block?.type).toLowerCase();

    if (type === "text") {
      const fontSizePx = Math.max(
        10,
        Math.min(42, asNumber(block.fontSize, 16))
      );

      const pdfFontSize = Math.max(
        8,
        Math.min(26, fontSizePx * 0.55)
      );

      const alignment = ["left", "center", "right"].includes(block.align)
        ? block.align
        : "left";

      pdf.setFont("helvetica", block.bold ? "bold" : "normal");
      pdf.setFontSize(pdfFontSize);
      pdf.setTextColor(30);

      const lines = pdf.splitTextToSize(
        mergeProofValue(
          String(block.text || ""),
          mergeContext
        ),
        contentWidth
      );

      const height = Math.max(
        6,
        lines.length * (pdfFontSize * 0.42 + 1)
      );

      newPageIfNeeded(height + 3);

      const x =
        alignment === "center"
          ? pageWidth / 2
          : alignment === "right"
            ? pageWidth - margin
            : margin;

      pdf.text(lines.length ? lines : [" "], x, y, {
        align: alignment,
      });

      y += height + 3;
      continue;
    }

    if (type === "image") {
      const widthPercent = Math.max(
        20,
        Math.min(100, asNumber(block.widthPercent, 100))
      );

      const drawWidth = contentWidth * (widthPercent / 100);

      const defaultHeight = Math.min(
        95,
        Math.max(35, drawWidth * 0.56)
      );

      newPageIfNeeded(defaultHeight + 8);

      const alignment = ["left", "center", "right"].includes(block.align)
        ? block.align
        : "center";

      const x =
        alignment === "left"
          ? margin
          : alignment === "right"
            ? pageWidth - margin - drawWidth
            : (pageWidth - drawWidth) / 2;

      const imageData = await loadImageAsDataUrl({
        url: block.imageUrl,
        storagePath: block.storagePath,
        venueId: options.venueId,
      });

      if (imageData) {
        try {
          const props = pdf.getImageProperties(imageData);

          const ratio =
            props?.width && props?.height
              ? props.height / props.width
              : 0.56;

          const drawHeight = Math.min(
            115,
            Math.max(18, drawWidth * ratio)
          );

          newPageIfNeeded(drawHeight + 8);

          pdf.addImage(
            imageData,
            imageFormatFromDataUrl(imageData),
            x,
            y,
            drawWidth,
            drawHeight,
            undefined,
            "FAST"
          );

          y += drawHeight + 6;
          continue;
        } catch (error) {
          console.warn("[mailerPdf] jsPDF could not embed image.", error);
        }
      }

      pdf.setDrawColor(140);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.rect(x, y, drawWidth, defaultHeight);
      pdf.setLineDashPattern([], 0);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(110);

      pdf.text(
        imageData ? "IMAGE PREVIEW UNAVAILABLE" : "IMAGE PLACEHOLDER",
        x + drawWidth / 2,
        y + defaultHeight / 2,
        {
          align: "center",
        }
      );

      if (clean(block.alt)) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);

        pdf.text(
          clean(block.alt),
          x + drawWidth / 2,
          y + defaultHeight / 2 + 6,
          {
            align: "center",
            maxWidth: drawWidth - 8,
          }
        );
      }

      y += defaultHeight + 6;
      continue;
    }

    if (type === "button") {
      const label =
        clean(
          mergeProofValue(
            block.label,
            mergeContext
          )
        ) || "Button";

      const buttonWidth = Math.min(
        contentWidth,
        Math.max(45, Math.min(120, label.length * 3.2 + 24))
      );

      const buttonHeight = 13;
      newPageIfNeeded(buttonHeight + 7);

      const x = (pageWidth - buttonWidth) / 2;

      pdf.setFillColor(55);
      pdf.roundedRect(x, y, buttonWidth, buttonHeight, 2, 2, "F");

      pdf.setTextColor(255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);

      pdf.text(label, pageWidth / 2, y + 8.3, {
        align: "center",
      });

      y += buttonHeight + 7;
      continue;
    }

    if (type === "divider") {
      newPageIfNeeded(7);

      pdf.setDrawColor(180);
      pdf.line(margin, y + 2, pageWidth - margin, y + 2);
      y += 7;
      continue;
    }

    if (type === "spacer") {
      const height = Math.max(
        3,
        Math.min(40, asNumber(block.height, 18) * 0.265)
      );

      newPageIfNeeded(height);
      y += height;
    }
  }

  const pageCount = pdf.getNumberOfPages();

  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(130);

    pdf.text(
      `CoreDeck Mailer proof • ${page}/${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      {
        align: "center",
      }
    );
  }

  const safeName =
    templateTitle(template)
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase() || "email-template";

  pdf.save(`${safeName}-proof.pdf`);
}
